import "../styles/globals.css";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useEffect } from "react";
import Script from "next/script";
import { GTM_ID, pageview } from "@/components/gtm";
import Head from "next/head";
import { generateDefaultSeo } from "next-seo/pages";
import { defaultSEO } from "@/utils/seo/defaultSEO";
import { ThemeProvider } from "next-themes";
import { checkAndSaveReferralCode } from "@/utils/referral";
import { captureUTMParams } from "@/utils/utmTracking";

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const isAnalyticsDisabled = process.env.NEXT_PUBLIC_DISABLE_ANALYTICS === "true";

  useEffect(() => {
    // Capture UTM parameters for attribution
    captureUTMParams();

    // Check for referral code in URL and save to cookie
    checkAndSaveReferralCode();

    if (isAnalyticsDisabled) return;

    // Send initial pageview
    pageview(window.location.pathname + window.location.search);

    // Track route changes
    router.events.on("routeChangeComplete", pageview);
    return () => {
      router.events.off("routeChangeComplete", pageview);
    };
  }, [router.events, isAnalyticsDisabled]);

  return (
    <>
      <Head>{generateDefaultSeo(defaultSEO)}</Head>
      {/* Google Tag Manager - Global base code */}
      {!isAnalyticsDisabled && (
        <Script
          id="gtag-base"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer', '${GTM_ID}');
            `,
          }}
        />
      )}
      {/* Ahrefs Web Analytics */}
      {!isAnalyticsDisabled && (
        <Script
          id="ahrefs-analytics"
          strategy="afterInteractive"
          src="https://analytics.ahrefs.com/analytics.js"
          data-key="VSLFIhd+VGrZ4jB20BhnDw"
        />
      )}
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        storageKey="uptime-theme"
      >
        <Component {...pageProps} />
      </ThemeProvider>
    </>
  );
}
